// ==============================================
// CO 3D VIEWER - Uses global THREE object
// ==============================================

(function() {
  'use strict';

  // ==============================================
  // CONFIGURATION
  // ==============================================

  var CONFIG = {
    // Camera
    cameraFov: 50,
    cameraNear: 0.1,
    cameraFar: 100,
    cameraZoomGel: 3.75,
    cameraZoomBox: 4.75,
    cameraZoomPack: 6.5,

    // Model
    modelScaleGel: 8,
    modelScaleBox: 7,
    modelScalePack: 6.5,
    modelRotationY: Math.PI / 2,

    // Auto rotation
    autoRotate: true,
    autoRotateSpeed: 0.12,

    // Controls
    enablePan: false,
    enableZoom: false,
    enableRotate: true,
    dampingEnabled: true,
    dampingFactor: 0.05,
    minPolarAngle: 0,
    maxPolarAngle: Math.PI / 2,

    // Renderer
    antialias: true,
    alpha: true,
    toneMapping: THREE.ReinhardToneMapping,
    toneMappingExposure: 2.0,
    pixelRatioLimit: 2,

    // Lighting
    lights: {
      keyLight: { position: [5, 8, 5], intensity: 4.5, color: 0xffffff },
      fillLight: { position: [-3, 4, 4], intensity: 0.4, color: 0xffffff },
      rimLightBack: { position: [0, 2, -8], intensity: 1.2, color: 0xffffff },
      frontLight: { position: [0, 2, 8], intensity: 0.7, color: 0xffffff },
      ambient: { intensity: 0.18, color: 0xffffff },
      pointLight1: { position: [4, 6, 2], intensity: 0.3, color: 0xffffff },
      pointLight2: { position: [-4, 3, 2], intensity: 0.15, color: 0xf8f8ff }
    },

    // Draco decoder path (CDN is fine here - it's loaded dynamically)
    dracoPath: 'https://unpkg.com/three@0.147.0/examples/js/libs/draco/'
  };

  // ==============================================
  // FLICKITY CONFIGURATION
  // ==============================================

  var FLICKITY_CONFIG = {
    sliderContainer: '.co-home-3 .main-carousel',
    scaleTarget: '.co-home-3-cell-frame',
    scaleActive: 1,
    scaleInactive: 0.5,
    options: {
      cellAlign: 'left',
      draggable: false,
      selectedAttraction: 0.01,
      friction: 0.2,
      contain: true,
      wrapAround: true,
      prevNextButtons: true,
      pageDots: true,
      arrowShape: ''
    }
  };

  // ==============================================
  // 3D VIEWER INITIALIZATION
  // ==============================================

  function initCo3DViewer(container) {
    var modelUrl = container.getAttribute('data-model');
    if (!modelUrl) {
      console.warn('co-3d-viewer: data-model attribute is missing');
      return;
    }

    var type = (container.getAttribute('data-type') || 'gel').toLowerCase();

    var scene = new THREE.Scene();

    var width = container.clientWidth;
    var height = container.clientHeight || (container.offsetWidth * 0.75) || 400;

    var camera = new THREE.PerspectiveCamera(
      CONFIG.cameraFov,
      width / height,
      CONFIG.cameraNear,
      CONFIG.cameraFar
    );

    var cameraZ = CONFIG.cameraZoomGel;
    if (type === 'box') cameraZ = CONFIG.cameraZoomBox;
    if (type === 'pack') cameraZ = CONFIG.cameraZoomPack;
    camera.position.set(0, 0, cameraZ);

    var renderer = new THREE.WebGLRenderer({
      antialias: CONFIG.antialias,
      alpha: CONFIG.alpha
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, CONFIG.pixelRatioLimit));
    renderer.toneMapping = CONFIG.toneMapping;
    renderer.toneMappingExposure = CONFIG.toneMappingExposure;

    container.appendChild(renderer.domElement);

    var group = new THREE.Group();
    scene.add(group);

    // Add lights
    addLights(group);

    // Controls
    var controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enablePan = CONFIG.enablePan;
    controls.enableZoom = CONFIG.enableZoom;
    controls.enableRotate = CONFIG.enableRotate;
    controls.enableDamping = CONFIG.dampingEnabled;
    controls.dampingFactor = CONFIG.dampingFactor;
    controls.minPolarAngle = CONFIG.minPolarAngle;
    controls.maxPolarAngle = CONFIG.maxPolarAngle;

    // Loaders
    var dracoLoader = new THREE.DRACOLoader();
    dracoLoader.setDecoderPath(CONFIG.dracoPath);

    var loader = new THREE.GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    var scaleVal = CONFIG.modelScaleGel;
    if (type === 'box') scaleVal = CONFIG.modelScaleBox;
    if (type === 'pack') scaleVal = CONFIG.modelScalePack;

    // Load model
    loader.load(
      modelUrl,
      function(gltf) {
        container.classList.add('co-ready');

        var model = gltf.scene;
        model.scale.set(scaleVal, scaleVal, scaleVal);
        model.rotation.set(0, CONFIG.modelRotationY, 0);

        var box = new THREE.Box3().setFromObject(model);
        var center = box.getCenter(new THREE.Vector3());

        model.position.x = -center.x;
        model.position.y = -center.y;
        model.position.z = -center.z;

        group.add(model);
      },
      undefined,
      function(error) {
        console.error('Error loading GLB model:', error);
        container.classList.add('co-error');
      }
    );

    // Resize handler
    function onResize() {
      var newWidth = container.clientWidth;
      var newHeight = container.clientHeight || (container.offsetWidth * 0.75) || 400;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    }

    window.addEventListener('resize', onResize);

    // Animation loop
    var lastTime = performance.now();

    function animate(now) {
      requestAnimationFrame(animate);
      var delta = (now - lastTime) / 1000;
      lastTime = now;

      if (CONFIG.autoRotate) {
        group.rotation.y += delta * CONFIG.autoRotateSpeed;
      }

      controls.update();
      renderer.render(scene, camera);
    }

    animate(performance.now());
  }

  // ==============================================
  // LIGHTING SETUP
  // ==============================================

  function addLights(parentGroup) {
    var L = CONFIG.lights;

    var keyLight = new THREE.DirectionalLight(L.keyLight.color, L.keyLight.intensity);
    keyLight.position.set(L.keyLight.position[0], L.keyLight.position[1], L.keyLight.position[2]);
    parentGroup.add(keyLight);

    var fillLight = new THREE.DirectionalLight(L.fillLight.color, L.fillLight.intensity);
    fillLight.position.set(L.fillLight.position[0], L.fillLight.position[1], L.fillLight.position[2]);
    parentGroup.add(fillLight);

    var rimLightBack = new THREE.DirectionalLight(L.rimLightBack.color, L.rimLightBack.intensity);
    rimLightBack.position.set(L.rimLightBack.position[0], L.rimLightBack.position[1], L.rimLightBack.position[2]);
    parentGroup.add(rimLightBack);

    var frontLight = new THREE.DirectionalLight(L.frontLight.color, L.frontLight.intensity);
    frontLight.position.set(L.frontLight.position[0], L.frontLight.position[1], L.frontLight.position[2]);
    parentGroup.add(frontLight);

    var ambient = new THREE.AmbientLight(L.ambient.color, L.ambient.intensity);
    parentGroup.add(ambient);

    var point1 = new THREE.PointLight(L.pointLight1.color, L.pointLight1.intensity);
    point1.position.set(L.pointLight1.position[0], L.pointLight1.position[1], L.pointLight1.position[2]);
    parentGroup.add(point1);

    var point2 = new THREE.PointLight(L.pointLight2.color, L.pointLight2.intensity);
    point2.position.set(L.pointLight2.position[0], L.pointLight2.position[1], L.pointLight2.position[2]);
    parentGroup.add(point2);
  }

  // ==============================================
  // FLICKITY INTEGRATION
  // ==============================================

  function initFlickitySlider() {
    var carousel = document.querySelector(FLICKITY_CONFIG.sliderContainer);
    if (!carousel || typeof Flickity === 'undefined') return;

    var flkty = new Flickity(carousel, FLICKITY_CONFIG.options);
    var initializedViewers = new WeakSet();

    function initVisibleViewers() {
      var selectedCell = flkty.selectedElement;
      if (!selectedCell) return;

      var viewer = selectedCell.querySelector('.co-3d-viewer');
      if (viewer && !initializedViewers.has(viewer)) {
        initCo3DViewer(viewer);
        initializedViewers.add(viewer);
      }
    }

    function updateSlideScale() {
      var viewport = flkty.viewport;
      var viewportCenter = viewport.offsetWidth / 2;

      flkty.cells.forEach(function(cell) {
        var slide = cell.element;
        var scaleTarget = slide.querySelector(FLICKITY_CONFIG.scaleTarget);

        if (!scaleTarget) return;

        scaleTarget.style.transformOrigin = 'center center';

        var slideRect = slide.getBoundingClientRect();
        var viewportRect = viewport.getBoundingClientRect();
        var slideCenterX = slideRect.left + slideRect.width / 2 - viewportRect.left;
        var slideWidth = slideRect.width;

        var distanceFromCenter = Math.abs(slideCenterX - viewportCenter) / slideWidth;

        var scale;
        if (distanceFromCenter <= 1) {
          var scaleDifference = FLICKITY_CONFIG.scaleActive - FLICKITY_CONFIG.scaleInactive;
          scale = FLICKITY_CONFIG.scaleActive - (distanceFromCenter * scaleDifference);
          scale = Math.max(scale, FLICKITY_CONFIG.scaleInactive);
        } else {
          scale = FLICKITY_CONFIG.scaleInactive;
        }

        scaleTarget.style.transform = 'scale(' + scale + ')';
      });
    }

    // Initialize
    initVisibleViewers();

    // Events
    flkty.on('change', initVisibleViewers);
    flkty.on('scroll', updateSlideScale);
    flkty.on('ready', updateSlideScale);
    flkty.on('settle', updateSlideScale);
  }

  // ==============================================
  // INITIALIZATION
  // ==============================================

  // Wait for DOM and THREE to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFlickitySlider);
  } else {
    initFlickitySlider();
  }

  // Expose for manual initialization if needed
  window.Co3DViewer = {
    init: initCo3DViewer,
    initSlider: initFlickitySlider,
    CONFIG: CONFIG
  };

})();
